import React, { useState, FocusEvent, ChangeEvent, useEffect, useRef } from 'react';
import useData from 'hooks/useData';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'components/Tooltip/Tooltip';
import classes from './EditableModulationCell.module.scss';

const ModulationCellContent = ({
    isCellChanged,
    elementId,
    modulationValue,
    canBeEdited,
    currentValue,
    errorMessage,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
}: {
    isCellChanged: boolean;
    elementId: string;
    modulationValue: number | string;
    canBeEdited: boolean;
    currentValue: number;
    errorMessage: string | null;
    onChange: ({ target: { value } }: ChangeEvent<HTMLInputElement>) => void;
    onFocus: (event: FocusEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
}) => {
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const { t } = useTranslation();
    const ref = useRef<HTMLInputElement>(null);

    const handleMouseEnter = () => {
        if (errorMessage) {
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return canBeEdited ? (
        <>
            <input
                className={`${classes['input-cell']} ${isCellChanged ? classes['input-cell-edited'] : ''}`}
                id={elementId}
                type="number"
                inputMode="numeric"
                value={modulationValue}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                autoComplete="off"
                ref={ref}
            />
            <Tooltip isOpen={showTooltip} parentElement={ref.current} prefix={t('WARNING')} content={errorMessage ?? ''} />
        </>
    ) : (
        <span data-testid="editable-cell-span">{currentValue}</span>
    );
};

const ModulationCell = ({
    elementId,
    currentHours,
    currentMinutes,
    canBeEdited,
    isActive,
}: {
    elementId: string;
    currentHours: number;
    currentMinutes: number;
    canBeEdited: boolean;
    isActive: boolean;
}) => {
    const [modulationValue, setModulationValue] = useState<number | string>(currentHours);
    const [isFocused, setIsFocused] = useState(false);
    const { currentScenario } = useData();
    useEffect(() => {
        setModulationValue(currentHours);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentHours, currentMinutes]);

    const isCellChanged = Number(modulationValue) !== 0;

    const handleBlur = () => {
        setIsFocused(false);
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        event.target.select();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // const debouncedValidate = useCallback(
    //     debounce(
    //         (hours: number) => {
    //             const areHoursValid = validateModulationHours(hours);

    //             setIsValid(areHoursValid);
    //         },
    //         250,
    //         { trailing: true },
    //     ),
    //     [],
    // );

    const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        const val = value === '' ? value : Number(value);
        // debouncedValidate(Number(val));
        setModulationValue(val);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleBlur();
        }
    };

    return (
        <td
            data-testid="modulationCell"
            key={elementId}
            // ${isCellChanged ? classes['td-cell-edited'] : ''}
            className={`
            ${classes['td-cell']}
            ${isFocused ? classes['td-cell-focused'] : ''}
            ${currentScenario || isActive ? '' : classes.disabled}
            `}
        >
            <ModulationCellContent
                elementId={elementId}
                canBeEdited={canBeEdited}
                currentValue={currentHours}
                modulationValue={modulationValue}
                errorMessage={null}
                isCellChanged={isCellChanged}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            />
        </td>
    );
};

export default ModulationCell;
